'use client';

import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import type { Product } from '@/lib/shopify/types';
import ProductCard from '@/components/product/product-card';
import FilterSidebar, { PRICE_BANDS, type FilterState } from '@/components/collection/filter-sidebar';
import SortSelect, { type SortId } from '@/components/collection/sort-select';
import FilterChips, { type Chip } from '@/components/collection/filter-chips';

function parseList(value: string | null): string[] {
  if (!value) return [];
  return value.split(',').filter(Boolean);
}

export interface AttributeFacetOption {
  id: string;
  label: string;
  terms: string[];
}

export interface AttributeFacet {
  id: string;
  label: string;
  param: string;
  options: AttributeFacetOption[];
}

const ATTRIBUTE_FACET_RULES: AttributeFacet[] = [
  {
    id: 'finish',
    label: 'Finish',
    param: 'finish',
    options: [
      { id: 'brushed-brass', label: 'Brushed Brass', terms: ['brushed brass'] },
      { id: 'polished-chrome', label: 'Polished Chrome', terms: ['polished chrome'] },
      { id: 'matt-black', label: 'Matt Black', terms: ['matt black', 'matte black'] },
      { id: 'chrome', label: 'Chrome', terms: ['chrome'] },
      { id: 'brass', label: 'Brass', terms: ['brass'] },
      { id: 'black', label: 'Black', terms: ['black'] },
      { id: 'white', label: 'White', terms: ['white'] },
      { id: 'grey', label: 'Grey', terms: ['grey', 'gray'] },
      { id: 'nickel', label: 'Nickel', terms: ['nickel'] },
      { id: 'stainless-steel', label: 'Stainless Steel', terms: ['stainless steel'] },
    ],
  },
  {
    id: 'shape',
    label: 'Shape',
    param: 'shape',
    options: [
      { id: 'offset-quadrant', label: 'Offset Quadrant', terms: ['offset quadrant'] },
      { id: 'rectangular', label: 'Rectangular', terms: ['rectangular'] },
      { id: 'quadrant', label: 'Quadrant', terms: ['quadrant'] },
      { id: 'square', label: 'Square', terms: ['square'] },
      { id: 'round', label: 'Round', terms: ['round'] },
      { id: 'oval', label: 'Oval', terms: ['oval'] },
      { id: 'l-shape', label: 'L Shape', terms: ['l shape', 'l-shape'] },
    ],
  },
  {
    id: 'mounting',
    label: 'Mounting',
    param: 'mounting',
    options: [
      { id: 'wall-mounted', label: 'Wall Mounted', terms: ['wall mounted', 'wall-mounted'] },
      { id: 'wall-hung', label: 'Wall Hung', terms: ['wall hung', 'wall-hung'] },
      { id: 'ceiling', label: 'Ceiling', terms: ['ceiling'] },
      { id: 'freestanding', label: 'Freestanding', terms: ['freestanding', 'free standing'] },
      { id: 'floorstanding', label: 'Floorstanding', terms: ['floorstanding', 'floor standing'] },
      { id: 'countertop', label: 'Countertop', terms: ['countertop', 'counter top'] },
    ],
  },
];

function normalizeFacetText(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function productFacetText(product: Product) {
  return normalizeFacetText([
    product.title,
    product.productType,
    product.vendor,
    ...product.tags,
    ...product.options.flatMap((option) => option.values),
  ].join(' '));
}

function getProductFacetOption(product: Product, facet: AttributeFacet): AttributeFacetOption | null {
  const text = productFacetText(product);

  return facet.options.find((option) =>
    option.terms.some((term) => text.includes(normalizeFacetText(term)))
  ) ?? null;
}

function buildAttributeFacets(products: Product[]): AttributeFacet[] {
  return ATTRIBUTE_FACET_RULES.map((facet) => {
    const counts = new Map<string, number>();

    for (const product of products) {
      const option = getProductFacetOption(product, facet);
      if (!option) continue;
      counts.set(option.id, (counts.get(option.id) ?? 0) + 1);
    }

    const options = facet.options.filter((option) => {
      const count = counts.get(option.id) ?? 0;
      return count > 0 && count < products.length;
    });

    return { ...facet, options };
  }).filter((facet) => facet.options.length >= 2);
}

function productPrice(p: Product): number {
  return parseFloat(p.priceRange.minVariantPrice.amount);
}

function matchPriceBand(p: Product, bandId: string): boolean {
  const band = PRICE_BANDS.find((b) => b.id === bandId);
  if (!band) return true;
  const price = productPrice(p);
  return price >= band.min && price < band.max;
}

function applyFilters(
  products: Product[],
  state: FilterState,
  attributeFacets: AttributeFacet[] = []
): Product[] {
  return products.filter((p) => {
    if (state.inStockOnly && !p.availableForSale) return false;
    if (state.brands.length && !state.brands.includes(p.vendor)) return false;
    if (state.price.length && !state.price.some((id) => matchPriceBand(p, id))) return false;
    for (const facet of attributeFacets) {
      const selected = state.attributes[facet.id] ?? [];
      if (selected.length === 0) continue;
      const option = getProductFacetOption(p, facet);
      if (!option || !selected.includes(option.id)) return false;
    }
    return true;
  });
}

function sortProducts(products: Product[], sortId: SortId): Product[] {
  const copy = [...products];
  switch (sortId) {
    case 'price-asc':
      return copy.sort((a, b) => productPrice(a) - productPrice(b));
    case 'price-desc':
      return copy.sort((a, b) => productPrice(b) - productPrice(a));
    case 'title-asc':
      return copy.sort((a, b) => a.title.localeCompare(b.title));
    case 'newest':
      // Products from Storefront API arrive newest-first by default when sorted by ID descending,
      // but we don't have a createdAt field — reverse the current order as a proxy.
      return copy.reverse();
    case 'featured':
    default:
      return copy;
  }
}

export default function CollectionClient({
  products,
  title,
  description,
  relatedCategories = [],
}: {
  products: Product[];
  title: string;
  description: string;
  relatedCategories?: {
    title: string;
    href: string;
    context: string;
  }[];
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const attributeFacets = useMemo(() => buildAttributeFacets(products), [products]);

  // Parse URL params → filter state
  const state: FilterState = useMemo(
    () => ({
      price: parseList(searchParams.get('price')),
      brands: parseList(searchParams.get('brand')),
      attributes: Object.fromEntries(
        attributeFacets.map((facet) => [facet.id, parseList(searchParams.get(facet.param))])
      ),
      inStockOnly: searchParams.get('stock') === '1',
    }),
    [attributeFacets, searchParams]
  );
  const sort = (searchParams.get('sort') as SortId) || 'featured';
  const PER_PAGE = 24;
  const parsedPage = parseInt(searchParams.get('page') ?? '1', 10);
  const currentPage = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  const gridTopRef = useRef<HTMLDivElement>(null);

  const updateUrl = useCallback(
    (next: {
      price?: string[];
      brands?: string[];
      attributes?: Record<string, string[]>;
      inStockOnly?: boolean;
      sort?: SortId;
      page?: number;
      keepPage?: boolean;
    }) => {
      const params = new URLSearchParams(searchParams.toString());

      const price = next.price ?? state.price;
      if (price.length) params.set('price', price.join(',')); else params.delete('price');

      const brands = next.brands ?? state.brands;
      if (brands.length) params.set('brand', brands.join(',')); else params.delete('brand');

      for (const facet of attributeFacets) {
        const selected = next.attributes?.[facet.id] ?? state.attributes[facet.id] ?? [];
        if (selected.length) params.set(facet.param, selected.join(',')); else params.delete(facet.param);
      }

      const stock = next.inStockOnly ?? state.inStockOnly;
      if (stock) params.set('stock', '1'); else params.delete('stock');

      const sortValue = next.sort ?? sort;
      if (sortValue && sortValue !== 'featured') params.set('sort', sortValue); else params.delete('sort');

      if (typeof next.page === 'number') {
        if (next.page > 1) params.set('page', String(next.page));
        else params.delete('page');
      } else if (!next.keepPage) {
        params.delete('page');
      }

      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [attributeFacets, pathname, router, searchParams, sort, state.price, state.brands, state.attributes, state.inStockOnly]
  );

  const togglePrice = useCallback((id: string) => {
    const next = state.price.includes(id)
      ? state.price.filter((p) => p !== id)
      : [...state.price, id];
    updateUrl({ price: next });
  }, [state.price, updateUrl]);

  const toggleBrand = useCallback((brand: string) => {
    const next = state.brands.includes(brand)
      ? state.brands.filter((b) => b !== brand)
      : [...state.brands, brand];
    updateUrl({ brands: next });
  }, [state.brands, updateUrl]);

  const toggleStock = useCallback(() => {
    updateUrl({ inStockOnly: !state.inStockOnly });
  }, [state.inStockOnly, updateUrl]);

  const toggleAttribute = useCallback((facetId: string, optionId: string) => {
    const current = state.attributes[facetId] ?? [];
    const next = current.includes(optionId)
      ? current.filter((id) => id !== optionId)
      : [...current, optionId];
    updateUrl({
      attributes: {
        ...state.attributes,
        [facetId]: next,
      },
    });
  }, [state.attributes, updateUrl]);

  const clearAll = useCallback(() => {
    updateUrl({
      price: [],
      brands: [],
      attributes: Object.fromEntries(attributeFacets.map((facet) => [facet.id, []])),
      inStockOnly: false,
    });
  }, [attributeFacets, updateUrl]);

  const setSort = useCallback((v: SortId) => {
    updateUrl({ sort: v });
  }, [updateUrl]);

  const allBrands = useMemo(
    () => [...new Set(products.map((p) => p.vendor).filter(Boolean))].sort(),
    [products]
  );

  // Facet counts — each count is against products filtered by *everything except* that same filter.
  const { brandCounts, priceCounts, attributeCounts, stockCount } = useMemo(() => {
    const brandCounts = new Map<string, number>();
    const priceCounts = new Map<string, number>();
    const attributeCounts = new Map<string, Map<string, number>>();

    // Brand counts: apply all filters except brand
    const forBrand = applyFilters(products, { ...state, brands: [] }, attributeFacets);
    for (const p of forBrand) {
      if (!p.vendor) continue;
      brandCounts.set(p.vendor, (brandCounts.get(p.vendor) ?? 0) + 1);
    }

    // Price counts: apply all filters except price
    const forPrice = applyFilters(products, { ...state, price: [] }, attributeFacets);
    for (const band of PRICE_BANDS) {
      let c = 0;
      for (const p of forPrice) {
        if (matchPriceBand(p, band.id)) c++;
      }
      priceCounts.set(band.id, c);
    }

    // Attribute counts: apply all filters except that same attribute.
    for (const facet of attributeFacets) {
      const counts = new Map<string, number>();
      const stateWithoutFacet = {
        ...state,
        attributes: {
          ...state.attributes,
          [facet.id]: [],
        },
      };
      const forAttribute = applyFilters(products, stateWithoutFacet, attributeFacets);

      for (const p of forAttribute) {
        const option = getProductFacetOption(p, facet);
        if (!option) continue;
        counts.set(option.id, (counts.get(option.id) ?? 0) + 1);
      }

      attributeCounts.set(facet.id, counts);
    }

    // Stock count: apply all filters except stock
    const forStock = applyFilters(products, { ...state, inStockOnly: false }, attributeFacets);
    const stockCount = forStock.filter((p) => p.availableForSale).length;

    return { brandCounts, priceCounts, attributeCounts, stockCount };
  }, [attributeFacets, products, state]);

  const filtered = useMemo(() => applyFilters(products, state, attributeFacets), [attributeFacets, products, state]);
  const sorted = useMemo(() => sortProducts(filtered, sort), [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paged = useMemo(
    () => sorted.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE),
    [sorted, safePage],
  );

  useEffect(() => {
    if (currentPage > totalPages && totalPages >= 1) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('page');
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    }
  }, [currentPage, totalPages, pathname, router, searchParams]);

  const goToPage = useCallback(
    (page: number) => {
      const clamped = Math.max(1, Math.min(page, totalPages));
      const params = new URLSearchParams(searchParams.toString());
      if (clamped > 1) params.set('page', String(clamped));
      else params.delete('page');
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
      if (typeof window !== 'undefined') {
        const top = gridTopRef.current?.getBoundingClientRect().top ?? 0;
        window.scrollBy({ top: top - 120, behavior: 'smooth' });
      }
    },
    [pathname, router, searchParams, totalPages],
  );

  const pageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    if (start > 2) pages.push('ellipsis');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push('ellipsis');
    pages.push(totalPages);
    return pages;
  }, [safePage, totalPages]);

  const activeCount =
    state.price.length +
    state.brands.length +
    Object.values(state.attributes).reduce((total, selected) => total + selected.length, 0) +
    (state.inStockOnly ? 1 : 0);

  const chips: Chip[] = useMemo(() => {
    const list: Chip[] = [];
    for (const id of state.price) {
      const band = PRICE_BANDS.find((b) => b.id === id);
      if (band) list.push({ key: `price:${id}`, label: band.label, onRemove: () => togglePrice(id) });
    }
    for (const b of state.brands) {
      list.push({ key: `brand:${b}`, label: b, onRemove: () => toggleBrand(b) });
    }
    for (const facet of attributeFacets) {
      for (const optionId of state.attributes[facet.id] ?? []) {
        const option = facet.options.find((facetOption) => facetOption.id === optionId);
        if (option) {
          list.push({
            key: `${facet.id}:${optionId}`,
            label: option.label,
            onRemove: () => toggleAttribute(facet.id, optionId),
          });
        }
      }
    }
    if (state.inStockOnly) {
      list.push({ key: 'stock', label: 'In stock only', onRemove: toggleStock });
    }
    return list;
  }, [attributeFacets, state, togglePrice, toggleBrand, toggleAttribute, toggleStock]);

  return (
    <section className="col-page">
      <div className="container">
        <header className="col-head">
          <h1 className="col-title">{title}</h1>
          {description && <p className="col-desc">{description}</p>}
          <p className="col-count">
            {sorted.length > 0 ? (
              <>
                Showing <strong>{(safePage - 1) * PER_PAGE + 1}–{Math.min(safePage * PER_PAGE, sorted.length)}</strong> of {sorted.length}
                {sorted.length !== products.length && <> (filtered from {products.length})</>}
              </>
            ) : (
              <>Showing <strong>0</strong> of {products.length} {products.length === 1 ? 'product' : 'products'}</>
            )}
          </p>
          {relatedCategories.length > 0 && (
            <nav className="col-related" aria-label="Related categories">
              <span className="col-related-label">Related categories</span>
              <div className="col-related-list">
                {relatedCategories.map((category) => (
                  <Link
                    key={category.href}
                    href={category.href}
                    className="col-related-chip"
                    title={category.context}
                  >
                    {category.title}
                  </Link>
                ))}
              </div>
            </nav>
          )}
        </header>

        <div className="col-toolbar-mobile">
          <button
            type="button"
            className="col-filter-btn"
            onClick={() => setDrawerOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M6 12h12M10 18h4" />
            </svg>
            Filter{activeCount > 0 ? ` (${activeCount})` : ''}
          </button>
          <SortSelect value={sort} onChange={setSort} />
        </div>

        <div className="col-layout">
          <aside className="col-sidebar">
            <FilterSidebar
              state={state}
              togglePrice={togglePrice}
              toggleBrand={toggleBrand}
              toggleStock={toggleStock}
              clearAll={clearAll}
              brandCounts={brandCounts}
              priceCounts={priceCounts}
              attributeFacets={attributeFacets}
              attributeCounts={attributeCounts}
              stockCount={stockCount}
              allBrands={allBrands}
              activeCount={activeCount}
              toggleAttribute={toggleAttribute}
            />
          </aside>

          <main className="col-main">
            <div className="col-toolbar-desktop">
              <FilterChips chips={chips} onClearAll={clearAll} />
              <div className="col-toolbar-right">
                <SortSelect value={sort} onChange={setSort} />
              </div>
            </div>

            <div ref={gridTopRef} />

            {sorted.length === 0 ? (
              <div className="col-empty">
                <p>No products match your filters.</p>
                {activeCount > 0 && (
                  <button type="button" className="col-empty-btn" onClick={clearAll}>
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="col-grid">
                  {paged.map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <nav className="col-pagination" aria-label="Pagination">
                    <button
                      type="button"
                      className="col-page-btn"
                      onClick={() => goToPage(safePage - 1)}
                      disabled={safePage <= 1}
                      aria-label="Previous page"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m15 18-6-6 6-6" />
                      </svg>
                      Prev
                    </button>

                    <ul className="col-page-list">
                      {pageNumbers.map((p, i) =>
                        p === 'ellipsis' ? (
                          <li key={`e-${i}`} className="col-page-ellipsis" aria-hidden="true">…</li>
                        ) : (
                          <li key={p}>
                            <button
                              type="button"
                              className={`col-page-num ${p === safePage ? 'active' : ''}`}
                              onClick={() => goToPage(p)}
                              aria-current={p === safePage ? 'page' : undefined}
                              aria-label={`Page ${p}`}
                            >
                              {p}
                            </button>
                          </li>
                        )
                      )}
                    </ul>

                    <button
                      type="button"
                      className="col-page-btn"
                      onClick={() => goToPage(safePage + 1)}
                      disabled={safePage >= totalPages}
                      aria-label="Next page"
                    >
                      Next
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </button>
                  </nav>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {drawerOpen && (
        <div className="col-drawer-overlay" onClick={() => setDrawerOpen(false)}>
          <div className="col-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="col-drawer-head">
              <span className="col-drawer-title">Filters</span>
              <button
                type="button"
                className="col-drawer-close"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close filters"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
            <div className="col-drawer-body">
              <FilterSidebar
                state={state}
                togglePrice={togglePrice}
                toggleBrand={toggleBrand}
                toggleStock={toggleStock}
                clearAll={clearAll}
                brandCounts={brandCounts}
                priceCounts={priceCounts}
                attributeFacets={attributeFacets}
                attributeCounts={attributeCounts}
                stockCount={stockCount}
                allBrands={allBrands}
                activeCount={activeCount}
                toggleAttribute={toggleAttribute}
              />
            </div>
            <div className="col-drawer-foot">
              <button
                type="button"
                className="col-drawer-apply"
                onClick={() => setDrawerOpen(false)}
              >
                Show {sorted.length} {sorted.length === 1 ? 'product' : 'products'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .col-page {
          padding: 1.5rem 0 3rem;
          background: var(--color-white);
        }
        .col-page .container { max-width: 1200px; }

        .col-head {
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid var(--color-border);
        }
        .col-title {
          font-size: clamp(1.5rem, 3vw, 2rem);
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 0.4rem;
        }
        .col-desc {
          font-size: 0.9rem;
          color: var(--color-text-secondary);
          line-height: 1.5;
          margin: 0 0 0.5rem;
          max-width: 720px;
        }
        .col-count {
          font-size: 0.8rem;
          color: var(--color-text-muted);
          margin: 0;
        }
        .col-count strong {
          color: var(--color-text-primary);
          font-weight: 700;
        }
        .col-related {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-top: 0.9rem;
          flex-wrap: wrap;
        }
        .col-related-label {
          padding-top: 0.34rem;
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--color-text-muted);
        }
        .col-related-list {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
          min-width: 0;
        }
        .col-related-chip {
          display: inline-flex;
          align-items: center;
          min-height: 2rem;
          padding: 0.38rem 0.7rem;
          border: 1px solid var(--color-border);
          border-radius: 999px;
          background: var(--color-white);
          color: var(--color-primary);
          font-size: 0.8rem;
          font-weight: 600;
          line-height: 1.2;
          text-decoration: none;
          transition: border-color 0.12s, background 0.12s;
        }
        .col-related-chip:hover {
          border-color: var(--color-primary);
          background: rgba(27,63,35,0.04);
        }

        .col-toolbar-mobile {
          display: none;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          margin-bottom: 1rem;
          padding: 0.6rem 0;
          position: sticky;
          top: 0;
          background: var(--color-white);
          z-index: 5;
        }
        .col-filter-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.5rem 0.9rem;
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.5rem;
          font-family: inherit;
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--color-text-primary);
          cursor: pointer;
        }
        .col-filter-btn:hover { background: var(--color-off-white); }
        .col-filter-btn svg { color: var(--color-primary); }

        .col-layout {
          display: grid;
          grid-template-columns: 260px minmax(0, 1fr);
          gap: 2rem;
          align-items: start;
        }
        .col-sidebar {
          position: sticky;
          top: 220px;
          align-self: start;
          height: fit-content;
          max-height: calc(100vh - 240px);
          overflow-y: auto;
          padding: 1rem 0.5rem 0 0;
        }
        .col-main {
          min-width: 0;
        }

        .col-toolbar-desktop {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          margin-bottom: 1.25rem;
          flex-wrap: wrap;
        }
        .col-toolbar-right { flex-shrink: 0; }

        .col-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }
        @media (max-width: 1280px) {
          .col-grid { grid-template-columns: repeat(3, 1fr); }
        }
        @media (max-width: 1023px) {
          .col-layout {
            grid-template-columns: 1fr;
            gap: 0;
          }
          .col-sidebar { display: none; }
          .col-toolbar-mobile { display: flex; }
          .col-toolbar-desktop {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75rem;
          }
          .col-toolbar-right { align-self: flex-end; }
          .col-grid { grid-template-columns: repeat(3, 1fr); }
        }
        @media (max-width: 640px) {
          .col-grid { grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
          .col-toolbar-desktop .sort { align-self: flex-start; }
          .col-toolbar-right { display: none; }
        }

        .col-empty {
          padding: 3rem 1rem;
          text-align: center;
          color: var(--color-text-secondary);
          border: 1px dashed var(--color-border);
          border-radius: 0.75rem;
        }
        .col-empty-btn {
          margin-top: 0.75rem;
          padding: 0.6rem 1.25rem;
          background: var(--color-primary);
          color: var(--color-white);
          border: none;
          border-radius: 0.5rem;
          font-family: inherit;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
        }
        .col-empty-btn:hover { background: var(--color-primary-dark); }

        /* Pagination */
        .col-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 0.4rem;
          margin: 2rem 0 0.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--color-border);
        }
        .col-page-btn,
        .col-page-num {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.35rem;
          min-width: 2.25rem;
          height: 2.25rem;
          padding: 0 0.8rem;
          background: var(--color-white);
          color: var(--color-text-primary);
          border: 1px solid var(--color-border);
          border-radius: 0.45rem;
          font-size: 0.85rem;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          transition: border-color 0.12s, color 0.12s, background 0.12s;
        }
        .col-page-btn:hover:not(:disabled),
        .col-page-num:hover:not(.active) {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }
        .col-page-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .col-page-list {
          display: inline-flex;
          list-style: none;
          margin: 0;
          padding: 0;
          gap: 0.35rem;
        }
        .col-page-num.active {
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: var(--color-white);
          cursor: default;
        }
        .col-page-ellipsis {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 1.5rem;
          height: 2.25rem;
          color: var(--color-text-muted);
          font-size: 0.85rem;
        }
        @media (max-width: 640px) {
          .col-page-btn { padding: 0 0.6rem; font-size: 0.8rem; }
          .col-page-num { min-width: 2rem; height: 2rem; padding: 0 0.5rem; font-size: 0.8rem; }
        }

        /* Mobile drawer */
        .col-drawer-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 200;
          display: flex;
          justify-content: flex-end;
        }
        .col-drawer {
          width: min(90vw, 360px);
          height: 100%;
          background: var(--color-white);
          display: flex;
          flex-direction: column;
        }
        .col-drawer-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 1.25rem;
          border-bottom: 1px solid var(--color-border);
        }
        .col-drawer-title {
          font-size: 1rem;
          font-weight: 700;
          color: var(--color-text-primary);
        }
        .col-drawer-close {
          background: transparent;
          border: none;
          padding: 0.3rem;
          cursor: pointer;
          color: var(--color-text-secondary);
          display: inline-flex;
        }
        .col-drawer-body {
          flex: 1;
          overflow-y: auto;
          padding: 1.25rem;
        }
        .col-drawer-foot {
          padding: 1rem 1.25rem;
          border-top: 1px solid var(--color-border);
          background: var(--color-white);
        }
        .col-drawer-apply {
          width: 100%;
          padding: 0.85rem 1rem;
          background: var(--color-primary);
          color: var(--color-white);
          border: none;
          border-radius: 0.75rem;
          font-family: inherit;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
        }
        .col-drawer-apply:hover { background: var(--color-primary-dark); }
      `}</style>
    </section>
  );
}
